<template>
    <div class="task-preview-widget">
        <div class="preview-header">
            <div class="header-content">
                <div class="header-icon">
                    <img src="@/assets/admin-icon/menu/tasks_active.svg" alt="Task" />
                </div>
                <div class="header-info">
                    <h3 class="task-title">{{ taskData.name || 'Task' }}</h3>
                    <p class="task-meta">
                        ID: {{ taskData.id }} • Created by {{ task_item.created_by }} • {{
                            formatDate(task_item.created_at) }}
                    </p>
                    <p class="task-meta" v-if="taskData.updated_at">
                        Last updated: {{ formatDate(taskData.updated_at) }}
                    </p>
                </div>
                <div class="priority-badge" v-if="false && taskData.priority !== undefined">
                    <span :class="getPriorityClass(taskData.priority)">
                        {{ getPriorityText(taskData.priority) }}
                    </span>
                </div>
            </div>
        </div>

        <div class="preview-body">
            <div class="task-details">
                <!-- Basic Info Section -->
                <div class="detail-section">
                    <h4 class="section-title">Task Details</h4>

                    <div class="detail-row" v-if="taskData.type">
                        <label>Type:</label>
                        <span class="task-type">{{ taskData.type }}</span>
                    </div>

                    <div class="detail-row" v-if="taskData.due_date">
                        <label>Due Date:</label>
                        <span class="due-date">{{ formatDate(taskData.due_date) }}</span>
                    </div>

                    <div class="detail-row" v-if="taskData.from_date">
                        <label>From Date:</label>
                        <span class="date-badge">{{ formatDate(taskData.from_date) }}</span>
                    </div>

                    <div class="detail-row" v-if="taskData.to_date">
                        <label>To Date:</label>
                        <span class="date-badge">{{ formatDate(taskData.to_date) }}</span>
                    </div>


                </div>

                <!-- Account Information -->
                <div class="detail-section" v-if="taskData.account">
                    <h4 class="section-title">Account Information</h4>

                    <div class="detail-row">
                        <label>Account:</label>
                        <span class="account-name">{{ taskData.account.name }}</span>
                    </div>

                    <div class="detail-row" v-if="taskData.account.email">
                        <label>Email:</label>
                        <span class="email-link">{{ taskData.account.email }}</span>
                    </div>

                    <div class="detail-row" v-if="taskData.account.phone">
                        <label>Phone:</label>
                        <span>{{ taskData.account.phone }}</span>
                    </div>

                    <div class="detail-row" v-if="taskData.account.website">
                        <label>Website:</label>
                        <span class="website-link">{{ taskData.account.website }}</span>
                    </div>

                    <div class="detail-row" v-if="taskData.account.location">
                        <label>Location:</label>
                        <span>{{ taskData.account.location }}</span>
                    </div>

                    <div class="detail-row" v-if="taskData.account.status">
                        <label>Status:</label>
                        <span :class="getAccountStatusClass(taskData.account.status)">
                            {{ taskData.account.status }}
                        </span>
                    </div>
                </div>

                <!-- Assignees Section -->
                <div class="detail-section" v-if="taskData.assignees && taskData.assignees.length > 0">
                    <h4 class="section-title">Responsible</h4>
                    <div class="assignees-list">
                        <div class="assignee-item" v-for="assignee in taskData.assignees" :key="assignee.id">
                            <div class="assignee-avatar">
                                <widgetProfile :title="assignee.first_name + ' ' + assignee.last_name"
                                    :showContent="false" subtitle="" file_name="" path=""></widgetProfile>
                            </div>
                            <div class="assignee-info">
                                <span class="assignee-name">{{ assignee.first_name }} {{ assignee.last_name }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Contacts Section -->
                <div class="detail-section" v-if="taskData.contacts && taskData.contacts.length > 0">
                    <h4 class="section-title">Related Contacts</h4>
                    <div class="contacts-list">
                        <div class="contact-item" v-for="contact in taskData.contacts" :key="contact.id">
                            <div class="contact-info">
                                <span class="contact-name">{{ contact.first_name }} {{ contact.last_name }}</span>
                                <span class="contact-email" v-if="contact.email">{{ contact.email }}</span>
                                <span class="contact-title" v-if="contact.title">{{ contact.title }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "TaskPreviewWidget",
    props: {
        task_item: {
            type: Object,
            required: true,
        },
    },
    computed: {
        taskData() {
            try {
                return JSON.parse(this.task_item.content || '{}');
            } catch (error) {
                console.error('Error parsing task content:', error);
                return {};
            }
        },
    },
    methods: {
        formatDate(date) {
            if (!date) return '';
            return this.moment(date).format('MMM DD, YYYY');
        },
        getPriorityClass(priority) {
            const priorityLevel = parseInt(priority);
            if (priorityLevel >= 8) return 'priority-high';
            if (priorityLevel >= 5) return 'priority-medium';
            return 'priority-low';
        },
        getPriorityText(priority) {
            const priorityLevel = parseInt(priority);
            if (priorityLevel >= 8) return 'High';
            if (priorityLevel >= 5) return 'Medium';
            return 'Low';
        },
        getAccountStatusClass(status) {
            return `account-status-${status}`;
        },
        getProfilePicture(filename) {
            // Adjust this path based on your backend configuration
            return `/uploads/profiles/${filename}`;
        },
        getInitials(firstName, lastName) {
            const first = firstName ? firstName.charAt(0).toUpperCase() : '';
            const last = lastName ? lastName.charAt(0).toUpperCase() : '';
            return first + last;
        },
    },
};
</script>

<style lang="scss" scoped>
.task-preview-widget {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #fff;

    .preview-header {
        padding: 20px;
        border-bottom: 1px solid #eee;
        background: #f8f9fa;

        .header-content {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;

            .header-icon {
                margin-right: 15px;

                img {
                    width: 32px;
                    height: 32px;
                }
            }

            .header-info {
                flex: 1;

                .task-title {
                    margin: 0 0 5px 0;
                    font-size: 18px;
                    font-weight: 600;
                    color: #333;
                }

                .task-meta {
                    margin: 0 0 3px 0;
                    color: #666;
                }
            }

            .priority-badge {
                .priority-high {
                    background: #ffebee;
                    color: #d32f2f;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-weight: 600;
                    text-transform: uppercase;
                }

                .priority-medium {
                    background: #fff3e0;
                    color: #f57c00;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-weight: 600;
                    text-transform: uppercase;
                }

                .priority-low {
                    background: #e8f5e8;
                    color: #2e7d32;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-weight: 600;
                    text-transform: uppercase;
                }
            }
        }
    }

    .preview-body {
        flex: 1;
        padding: 20px;
        overflow-y: auto;

        .task-details {
            .detail-section {
                margin-bottom: 25px;

                .section-title {
                    font-size: 14px;
                    font-weight: 600;
                    color: #333;
                    margin: 0 0 15px 0;
                    padding-bottom: 8px;
                    border-bottom: 2px solid #e3f2fd;
                }

                .detail-row {
                    display: flex;
                    align-items: center;
                    margin-bottom: 12px;
                    padding: 10px;
                    background: #f8f9fa;
                    border-radius: 8px;

                    label {
                        font-weight: 600;
                        color: #555;
                        min-width: 120px;
                        margin-right: 10px;
                    }

                    span {
                        color: #333;

                        &.task-type {
                            background: #e3f2fd;
                            color: #1976d2;
                            padding: 4px 12px;
                            border-radius: 20px;
                            font-weight: 500;
                        }

                        &.due-date {
                            background: #fff3e0;
                            color: #f57c00;
                            padding: 4px 12px;
                            border-radius: 20px;
                            font-weight: 500;
                        }

                        &.date-badge {
                            background: #f3e5f5;
                            color: #7b1fa2;
                            padding: 4px 12px;
                            border-radius: 20px;
                            font-weight: 500;
                        }

                        &.account-name {
                            font-weight: 600;
                            color: #1976d2;
                        }

                        &.email-link {
                            color: #1976d2;
                            text-decoration: underline;
                        }

                        &.website-link {
                            color: #1976d2;
                            text-decoration: underline;
                        }

                        &.account-status-active {
                            background: #e8f5e8;
                            color: #2e7d32;
                            padding: 3px 8px;
                            border-radius: 12px;
                            font-weight: 600;
                            text-transform: capitalize;
                        }

                        &.account-status-passive {
                            background: #fff3e0;
                            color: #f57c00;
                            padding: 3px 8px;
                            border-radius: 12px;
                            font-weight: 600;
                            text-transform: capitalize;
                        }

                        &.account-status-inactive {
                            background: #ffebee;
                            color: #d32f2f;
                            padding: 3px 8px;
                            border-radius: 12px;
                            font-weight: 600;
                            text-transform: capitalize;
                        }
                    }
                }
            }

            .assignees-list {
                .assignee-item {
                    display: flex;
                    align-items: center;
                    padding: 10px;
                    background: #f8f9fa;
                    border-radius: 8px;
                    margin-bottom: 8px;

                    .assignee-avatar {
                        margin-right: 12px;

                        img {
                            width: 32px;
                            height: 32px;
                            border-radius: 50%;
                            object-fit: cover;
                        }

                        .avatar-placeholder {
                            width: 32px;
                            height: 32px;
                            border-radius: 50%;
                            background: #1976d2;
                            color: white;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-weight: 600;
                        }
                    }

                    .assignee-info {
                        .assignee-name {
                            font-weight: 600;
                            color: #333;
                        }
                    }
                }
            }

            .contacts-list {
                .contact-item {
                    padding: 10px;
                    background: #f8f9fa;
                    border-radius: 8px;
                    margin-bottom: 8px;

                    .contact-info {
                        display: flex;
                        flex-direction: column;
                        gap: 3px;

                        .contact-name {
                            font-weight: 600;
                            color: #333;
                        }

                        .contact-email {
                            color: #1976d2;
                        }

                        .contact-title {
                            color: #666;
                            font-style: italic;
                        }
                    }
                }
            }
        }
    }
}
</style>